/**
 * 拣选策略配置管理页面
 * 展示当前门店的拣选策略配置，支持编辑（单 Drawer 包含全部配置）
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Button, Card, Table, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { EditOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import type { PickingStrategyConfigDTO } from '../../types/picking-strategy/pickingStrategy';
import * as pickingStrategyService from '../../services/picking-strategy/pickingStrategyService';
import EllipsisCell from '@/components/common/EllipsisCell';
import PickingStrategyForm from './components/PickingStrategyForm';

// TODO: 生产环境从认证上下文获取当前门店编码
const CURRENT_STORE_CODE = 'STORE001';

const PickingStrategyList: React.FC = () => {
  const { t } = useTranslation();

  // 当前门店配置
  const [loading, setLoading] = useState(false);
  const [currentConfig, setCurrentConfig] = useState<PickingStrategyConfigDTO | null>(null);

  // Drawer 状态
  const [formVisible, setFormVisible] = useState(false);

  // 加载当前门店配置
  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const response = await pickingStrategyService.getPickingStrategyConfig();
      if (response.success) {
        setCurrentConfig(response.data);
      } else {
        message.error(response.errorMessage || t('common.message.loadFailed'));
      }
    } catch {
      message.error(t('common.message.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const columns: ColumnsType<PickingStrategyConfigDTO> = [
    {
      title: t('pickingStrategy.list.column.company'),
      key: 'company',
      width: '33%',
      render: (_, record) => (
        <EllipsisCell>
          <span>
            <strong>{record.company || '-'}</strong>
          </span>
        </EllipsisCell>
      ),
    },
    {
      title: t('pickingStrategy.list.column.updatedBy'),
      dataIndex: 'updatedBy',
      key: 'updatedBy',
      width: '33%',
      render: (val: string) => <EllipsisCell>{val || '-'}</EllipsisCell>,
    },
    {
      title: t('pickingStrategy.list.column.updatedTime'),
      dataIndex: 'updatedTime',
      key: 'updatedTime',
      width: '34%',
      render: (val: number) =>
        <EllipsisCell>{val ? dayjs(val).format('DD-MM-YYYY HH:mm:ss') : '-'}</EllipsisCell>,
    },
    {
      title: t('pickingStrategy.list.column.action'),
      key: 'action',
      width: 100,
      fixed: 'right',
      render: () => (
        <Button
          type="link"
          icon={<EditOutlined />}
          onClick={() => setFormVisible(true)}
        >
          {t('pickingStrategy.list.column.actionEdit')}
        </Button>
      ),
    },
  ];

  return (
    <div style={{ padding: '16px', height: '100%' }}>
      <Card style={{ height: '100%' }}>
        <Table<PickingStrategyConfigDTO>
          rowKey="storeCode"
          columns={columns}
          dataSource={currentConfig ? [currentConfig] : []}
          loading={loading}
          pagination={false}
          locale={{ emptyText: t('common.message.noData') }}
          style={{ height: '100%' }}
        />
      </Card>

      {/* 拣选策略配置 Drawer（编辑） */}
      <PickingStrategyForm
        visible={formVisible}
        mode="edit"
        record={currentConfig}
        onClose={() => setFormVisible(false)}
        onSuccess={() => {
          setFormVisible(false);
          loadConfig();
        }}
      />
    </div>
  );
};

export default PickingStrategyList;
